// Owner-facing share management screen (spec #22).
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { sharesApi } from '@/api/sharesApi';
import { reportsApi } from '@/api/reportsApi';
import { ApiException } from '@/api/errors';
import { formatDate, formatRelative, SHARE_VISIBILITY_LABEL } from '@/utils/format';
import type { ReportJson, ReportShare } from '@/api/types';

export function SharesScreen() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [shares, setShares] = useState<ReportShare[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<ReportJson[]>([]);
  const [reportsBusy, setReportsBusy] = useState(true);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [visibility, setVisibility] = useState<'friends' | 'link'>('friends');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [expiryEnabled, setExpiryEnabled] = useState(false);
  const [lastCreated, setLastCreated] = useState<ReportShare | null>(null);

  async function loadShares() {
    setBusy(true);
    setError(null);
    try {
      const rows = await sharesApi.list(getToken);
      setShares(rows);
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not load shares.');
    } finally {
      setBusy(false);
    }
  }

  async function loadReports() {
    setReportsBusy(true);
    try {
      const { rows } = await reportsApi.list(getToken, { status: 'draft' });
      setReports(rows);
    } catch {
      // best-effort: a load failure here is non-fatal for the share UI.
    } finally {
      setReportsBusy(false);
    }
  }

  useEffect(() => {
    void loadShares();
    void loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreate() {
    if (!selectedReportId) {
      setCreateError('Pick a report to share first.');
      return;
    }
    setCreateBusy(true);
    setCreateError(null);
    try {
      const share = await sharesApi.create(getToken, {
        report_id: selectedReportId,
        visibility,
        expires_at: expiryEnabled && expiresAt ? new Date(expiresAt).toISOString() : undefined,
      });
      setLastCreated(share);
      await loadShares();
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setCreateError(err?.message ?? 'Could not create share.');
    } finally {
      setCreateBusy(false);
    }
  }

  async function handleRevoke(shareId: string) {
    if (!window.confirm('Revoke this share? Anyone with the link will lose access.')) return;
    try {
      await sharesApi.revoke(getToken, shareId);
      await loadShares();
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(err?.message ?? 'Could not revoke share.');
    }
  }

  async function handleCopy(shareUrl: string) {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      // best-effort fallback – silently ignore if the browser blocks the
      // clipboard, the user can still copy manually.
    }
  }

  return (
    <div className="stack">
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0 }}>Shared reports</h1>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              Create a link to share one of your reports with friends or the public.
            </p>
          </div>
          <button type="button" className="btn btn--primary" onClick={loadShares}>
            Refresh
          </button>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>New share</h2>
        {reportsBusy ? (
          <div className="empty">Loading reports…</div>
        ) : reports.length === 0 ? (
          <div className="empty">
            You don&apos;t have any draft reports yet.{' '}
            <Link to="/reports/new">Create a report first</Link>.
          </div>
        ) : (
          <div className="stack">
            <div className="field">
              <label htmlFor="share-report">Report</label>
              <select
                id="share-report"
                className="select"
                value={selectedReportId}
                onChange={(e) => setSelectedReportId(e.target.value)}
              >
                <option value="">Select a report…</option>
                {reports.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title.trim() === '' ? '(Untitled)' : r.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="share-visibility">Who can open this report?</label>
              <select
                id="share-visibility"
                className="select"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as 'friends' | 'link')}
              >
                <option value="friends">{SHARE_VISIBILITY_LABEL.friends}</option>
                <option value="link">{SHARE_VISIBILITY_LABEL.link}</option>
              </select>
            </div>
            <div className="field">
              <label>
                <input
                  type="checkbox"
                  checked={expiryEnabled}
                  onChange={(e) => setExpiryEnabled(e.target.checked)}
                />{' '}
                Set an expiry date
              </label>
              {expiryEnabled ? (
                <input
                  type="datetime-local"
                  className="input"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              ) : null}
            </div>
            {createError ? <div className="error-banner">{createError}</div> : null}
            <div>
              <button
                type="button"
                className="btn btn--primary"
                disabled={createBusy || !selectedReportId}
                onClick={handleCreate}
              >
                {createBusy ? 'Creating…' : 'Create share link'}
              </button>
            </div>
            {lastCreated?.share_url ? (
              <div className="info-banner stack">
                <div>
                  <strong>Share is ready.</strong> Send this link to your reviewer:
                </div>
                <code
                  style={{
                    display: 'block',
                    padding: 12,
                    background: 'var(--color-bg-muted)',
                    borderRadius: 6,
                    wordBreak: 'break-all',
                  }}
                >
                  {lastCreated.share_url}
                </code>
                <div>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => handleCopy(lastCreated.share_url!)}
                  >
                    Copy link
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>My shares</h2>
        {error ? <div className="error-banner">{error}</div> : null}
        {busy ? (
          <div className="empty">Loading…</div>
        ) : shares.length === 0 ? (
          <div className="empty">You have not shared any reports yet.</div>
        ) : (
          <div className="list">
            {shares.map((share) => (
              <div key={share.id} className="list__item" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <div className="list__item-title">
                    {share.share_url && !share.revoked_at ? (
                      <button
                        type="button"
                        className="btn btn--ghost"
                        style={{ padding: 0, border: 'none' }}
                        onClick={() => handleCopy(share.share_url!)}
                      >
                        {share.share_url}
                      </button>
                    ) : (
                      <span className="muted">
                        {share.report_title ?? 'Report'} {share.revoked_at ? '(revoked)' : ''}
                      </span>
                    )}
                  </div>
                  <div className="list__item-subtitle">
                    <span className="pill">
                      {SHARE_VISIBILITY_LABEL[share.visibility] ?? share.visibility}
                    </span>
                    <span>· Created {formatRelative(share.created_at)}</span>
                    {share.expires_at ? (
                      <span>· Expires {formatDate(share.expires_at)}</span>
                    ) : null}
                  </div>
                </div>
                <div className="row" style={{ gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => navigate(`/reports/${share.report_id}`)}
                  >
                    Open
                  </button>
                  {!share.revoked_at ? (
                    <button
                      type="button"
                      className="btn btn--danger"
                      onClick={() => handleRevoke(share.id)}
                    >
                      Revoke
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
